import 'core-js/modules/es.array.iterator.js';
import 'core-js/modules/web.dom-collections.iterator.js';
import 'core-js/modules/es.object.assign.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useUtility, Stack, Text } from '@hub-la/design-system';
import { AssetType, AssetStatus } from '@hub-la/fe-asset';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { LinearProgress } from '@mui/material';
import { useGetPdfFileUrl } from '../../hooks/use-get-pdf-file-url.js';
import useMeasure from '../../hooks/use-measure.js';
import { MediaDocumentPDF } from '../media-document-pdf/media-document-pdf.js';
import { Player } from '../player/index.js';
import { VideoProcessingCover } from '../video-processing-cover/video-processing-cover.js';
import { Wrapper } from './post-detail-cover.styled.js';

const ThumbnailOverlay = ({
  image,
  alt
}) => {
  return jsx("img", {
    src: image,
    alt: alt,
    style: {
      position: 'absolute',
      filter: 'blur(4px) brightness(0.2)',
      width: '104vw',
      zIndex: '1',
      height: 'auto',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%,-50%)'
    }
  });
};
const PostDetailCover = ({
  cover,
  customCover,
  sectionId,
  postId,
  hideContent,
  hideText,
  isLive: _isLive = false
}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
  const [ref, {
    height,
    width
  }] = useMeasure();
  const {
    palette
  } = useUtility();
  const hasContent = !!((_c = (_b = (_a = cover === null || cover === void 0 ? void 0 : cover.metadata) === null || _a === void 0 ? void 0 : _a.thumbnailUrl) !== null && _b !== void 0 ? _b : cover === null || cover === void 0 ? void 0 : cover.url) !== null && _c !== void 0 ? _c : customCover);
  const {
    data: pdfFileUrl
  } = useGetPdfFileUrl(cover);
  /** @description: Hide the content for users that don't have access */
  if (hasContent && hideContent) {
    return jsx(Wrapper, Object.assign({
      height: width * 0.5625,
      ref: ref,
      id: "detail-cover-box",
      started: _isLive
    }, {
      children: jsxs(Stack, Object.assign({
        position: "relative",
        height: "100%",
        "data-testid": "content-hidden"
      }, {
        children: [jsx("img", {
          src: (_f = (_e = (_d = cover === null || cover === void 0 ? void 0 : cover.metadata) === null || _d === void 0 ? void 0 : _d.thumbnailUrl) !== null && _e !== void 0 ? _e : cover === null || cover === void 0 ? void 0 : cover.url) !== null && _f !== void 0 ? _f : customCover,
          alt: 'hidden',
          style: {
            height: '100%',
            width: '100%',
            objectFit: 'cover',
            borderRadius: '8px',
            filter: 'blur(40px) brightness(0.3)'
          }
        }), jsxs(Stack, Object.assign({
          direction: "column",
          position: "absolute",
          top: "50%",
          left: "50%",
          alignItems: "center",
          spacing: 2,
          style: {
            transform: 'translate(-50%, -50%)'
          }
        }, {
          children: [jsx(LockOutlinedIcon, {
            fontSize: "large",
            style: {
              color: palette('onSurfaceVariant')
            }
          }), jsx(Text, Object.assign({
            color: "onSurfaceVariant",
            variant: "body1"
          }, {
            children: hideText
          }))]
        }))]
      }))
    }));
  }
  /** @description: If it does not have a cover, but has a customCover, display it */
  if (!cover && customCover) {
    return jsx(Wrapper, Object.assign({
      height: width * 0.5625,
      ref: ref,
      id: "detail-cover-box",
      started: _isLive
    }, {
      children: jsxs(Stack, Object.assign({
        position: "relative",
        height: "100%",
        "data-testid": "custom-cover"
      }, {
        children: [jsx("img", {
          src: customCover,
          alt: 'capa',
          style: {
            zIndex: 2,
            margin: 'auto',
            height: '80%',
            maxWidth: '100vw',
            borderRadius: '8px'
          }
        }), jsx(ThumbnailOverlay, {
          image: customCover,
          alt: 'capa'
        })]
      }))
    }));
  }
  return jsxs(Wrapper, Object.assign({
    height: width * 0.5625,
    ref: ref,
    id: "detail-cover-box",
    started: _isLive
  }, {
    children: [(cover === null || cover === void 0 ? void 0 : cover.type) === AssetType.image && jsxs(Stack, Object.assign({
      position: "relative",
      height: "100%",
      "data-testid": "image-viewer"
    }, {
      children: [jsx("img", {
        src: cover.url,
        alt: cover.name,
        style: {
          zIndex: 2,
          margin: 'auto',
          height: '100%',
          maxWidth: '100vw',
          borderRadius: '8px'
        }
      }), jsx(ThumbnailOverlay, {
        image: cover.url,
        alt: cover.name
      })]
    })), (cover === null || cover === void 0 ? void 0 : cover.type) === AssetType.application && jsxs(Stack, Object.assign({
      style: {
        backgroundColor: palette('background')
      },
      position: "relative",
      p: 4,
      "data-testid": "pdf-viewer",
      height: "100%"
    }, {
      children: [!pdfFileUrl && jsx(LinearProgress, {
        color: "inherit",
        style: {
          height: '1px'
        }
      }), pdfFileUrl && jsx(MediaDocumentPDF, {
        progress: ((_g = cover.progress) === null || _g === void 0 ? void 0 : _g.currentProgress) || 1,
        previewHeight: height - 52,
        assetId: cover.id,
        url: pdfFileUrl,
        readonly: false,
        sectionId: sectionId,
        postId: postId
      })]
    })), (cover === null || cover === void 0 ? void 0 : cover.type) === AssetType.video && cover.status !== AssetStatus.processing && jsx(Stack, Object.assign({
      "data-testid": "video-player",
      height: "100%"
    }, {
      children: jsx(Player, {
        progress: (_h = cover.progress) === null || _h === void 0 ? void 0 : _h.currentProgress,
        assetId: cover.id,
        src: (_j = cover.url) !== null && _j !== void 0 ? _j : '',
        poster: (_k = cover.metadata) === null || _k === void 0 ? void 0 : _k.thumbnailUrl,
        title: cover.name,
        sectionId: sectionId,
        postId: postId
      })
    })), jsx(VideoProcessingCover, {
      cover: cover
    })]
  }), cover === null || cover === void 0 ? void 0 : cover.id);
};

export { PostDetailCover };
